import { AGENT_PATHS, type AgentEvent, type BuilderSession, previewLabel, QUOTAS } from '@xb/shared';
import { type FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import { api, errorMessage } from '../api.js';
import { useAction, useInterval, useResource } from '../hooks.js';
import { useApp } from '../pages/AppPage.js';
import { fmtTime } from './format.js';
import { ErrorNotice, Loading, Notice } from './Notice.js';

/**
 * The builder: chat with the Xero Builder agent on the left, the live preview on the right. The chat
 * streams from the sandbox itself (via the preview host) — the console API only starts and stops the
 * session. Works at localhost:5173 only via "open in a new tab" (the preview cookie is cross-site there).
 */
type ChatItem =
  | { kind: 'user'; seq: number; text: string; at: string }
  | { kind: 'assistant'; seq: number; text: string; at?: string; streaming?: boolean; turn: string }
  | {
      kind: 'tool';
      seq: number;
      name: string;
      summary: string;
      ok?: boolean;
      done: boolean;
      turn: string;
      toolUseId: string;
    }
  | { kind: 'publish'; seq: number; phase: string; message: string; turn: string }
  | { kind: 'notice'; seq: number; text: string; level: 'info' | 'error' };

export function BuilderTab() {
  const { app } = useApp();
  const [params] = useSearchParams();
  const status = useResource(() => api.builder.status(app.slug), [app.slug]);
  const session = status.data?.session ?? undefined;
  const active = !!session && (session.status === 'starting' || session.status === 'running');
  useInterval(status.reload, session?.status === 'starting' || session?.status === 'stopping' ? 3000 : null);

  const start = useAction(async () => {
    await api.builder.start(app.slug);
    status.reload();
  });
  const stop = useAction(async () => {
    if (!session) return;
    await api.builder.stop(app.slug, session.id);
    status.reload();
  });

  const previewOrigin = `https://${previewLabel(app.slug)}.${app.host.split('.').slice(1).join('.')}`;
  const isLocalDev = window.location.hostname === 'localhost';

  if (status.error) return <ErrorNotice error={status.error} />;
  if (!status.data) return <Loading what="Loading the builder" />;
  if (!status.data.enabled) {
    return (
      <Notice kind="warn">
        The builder is not enabled on this platform stage. Deploy the platform with the builder on (the
        default) to use it.
      </Notice>
    );
  }

  return (
    <>
      {params.get('preview') === 'stopped' ? (
        <Notice kind="info">That build session had ended. Start a new one below.</Notice>
      ) : null}
      <div className="page-head">
        <div>
          <h2 style={{ margin: 0 }}>Build with the Xero Builder agent</h2>
          <p className="small subtle" style={{ margin: '0.25rem 0 0' }}>
            Describe what you want; the agent writes the app and shows it live. Powered by Claude. Sessions
            stop after {QUOTAS.builderIdleMinutes} idle minutes and pick up where you left off.
          </p>
        </div>
        <div className="btn-row">
          {active ? (
            <>
              <a className="btn" href={`${previewOrigin}/`} target="_blank" rel="noreferrer">
                Open preview ↗
              </a>
              <button
                type="button"
                className="btn btn-danger"
                disabled={stop.pending}
                onClick={() => void stop.run()}
              >
                Stop session
              </button>
            </>
          ) : (
            <button
              type="button"
              className="btn btn-primary"
              disabled={start.pending || app.status === 'deleting' || app.status === 'failed'}
              onClick={() => void start.run()}
            >
              {start.pending ? 'Starting…' : session ? 'Resume building' : 'Start building'}
            </button>
          )}
        </div>
      </div>
      <ErrorNotice error={start.error ?? stop.error} />
      {session?.status === 'starting' ? (
        <Notice kind="info">Setting up your workspace… this takes about a minute the first time.</Notice>
      ) : null}
      {session?.status === 'failed' && session.stoppedReason ? (
        <Notice kind="warn">The last session failed: {session.stoppedReason}</Notice>
      ) : null}
      {session?.status === 'running' ? (
        isLocalDev ? (
          <Notice kind="info">
            Local development: the preview cookie does not work in an iframe from localhost.{' '}
            <a href={`${previewOrigin}/`} target="_blank" rel="noreferrer">
              Open the preview in a new tab
            </a>{' '}
            and chat from there is not available yet; deploy the console to use the embedded builder.
          </Notice>
        ) : (
          <Workspace
            app={app}
            session={session}
            previewOrigin={previewOrigin}
            onSessionEnded={status.reload}
          />
        )
      ) : null}
    </>
  );
}

function Workspace({
  session,
  previewOrigin,
  onSessionEnded,
}: {
  app: ReturnType<typeof useApp>['app'];
  session: BuilderSession;
  previewOrigin: string;
  onSessionEnded: () => void;
}) {
  const [items, setItems] = useState<ChatItem[]>([]);
  const [busy, setBusy] = useState(false);
  const [connected, setConnected] = useState(false);
  const [preview, setPreview] = useState<{ state: string; detail?: string }>({ state: 'starting' });
  const [usage, setUsage] = useState<{ tokens: number; cost?: number }>({ tokens: 0 });
  const [frameKey, setFrameKey] = useState(0);
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<unknown>(undefined);
  const lastSeq = useRef(0);
  const listRef = useRef<HTMLDivElement>(null);
  const streamRef = useRef<{ turn: string; text: string } | undefined>(undefined);

  const apply = useCallback(
    (e: AgentEvent) => {
      if (e.seq <= lastSeq.current && e.type !== 'assistant.delta') return;
      if (e.type !== 'assistant.delta') lastSeq.current = e.seq;
      setItems((prev) => {
        switch (e.type) {
          case 'hello':
            setBusy(e.busy);
            return prev;
          case 'user':
            setBusy(true);
            return [...prev, { kind: 'user', seq: e.seq, text: e.text, at: e.at }];
          case 'assistant.delta': {
            const s = streamRef.current;
            const cur =
              s && s.turn === e.turn
                ? { turn: s.turn, text: s.text + e.text }
                : { turn: e.turn, text: e.text };
            streamRef.current = cur;
            const idx = prev.findIndex((i) => i.kind === 'assistant' && i.streaming && i.turn === cur.turn);
            const item: ChatItem = {
              kind: 'assistant',
              seq: e.seq,
              text: cur.text,
              streaming: true,
              turn: cur.turn,
            };
            if (idx === -1) return [...prev, item];
            const next = prev.slice();
            next[idx] = item;
            return next;
          }
          case 'assistant.message': {
            streamRef.current = undefined;
            const idx = prev.findIndex((i) => i.kind === 'assistant' && i.streaming && i.turn === e.turn);
            const item: ChatItem = { kind: 'assistant', seq: e.seq, text: e.text, at: e.at, turn: e.turn };
            if (idx === -1) return [...prev, item];
            const next = prev.slice();
            next[idx] = item;
            return next;
          }
          case 'tool.start':
            return [
              ...prev,
              {
                kind: 'tool',
                seq: e.seq,
                name: e.name,
                summary: e.summary,
                done: false,
                turn: e.turn,
                toolUseId: e.toolUseId,
              },
            ];
          case 'tool.end': {
            const idx = prev.findIndex((i) => i.kind === 'tool' && i.toolUseId === e.toolUseId);
            if (idx === -1) return prev;
            const next = prev.slice();
            const t = next[idx] as Extract<ChatItem, { kind: 'tool' }>;
            next[idx] = { ...t, done: true, ok: e.ok };
            return next;
          }
          case 'publish.progress': {
            const idx = prev.findIndex((i) => i.kind === 'publish' && i.turn === e.turn);
            const item: ChatItem = {
              kind: 'publish',
              seq: e.seq,
              phase: e.phase,
              message: e.message,
              turn: e.turn,
            };
            if (idx === -1) return [...prev, item];
            const next = prev.slice();
            next[idx] = item;
            return next;
          }
          case 'turn.complete':
            setBusy(false);
            streamRef.current = undefined;
            setUsage({ tokens: e.tokensUsed, cost: e.costUsd });
            setFrameKey((k) => k + 1);
            return e.ok || !e.error
              ? prev
              : [
                  ...prev,
                  { kind: 'notice', seq: e.seq, text: `The agent stopped: ${e.error}`, level: 'error' },
                ];
          case 'preview.status':
            setPreview({ state: e.state, detail: e.detail });
            if (e.state === 'ready') setFrameKey((k) => k + 1);
            return prev;
          case 'usage':
            setUsage((u) => ({ ...u, tokens: e.tokensUsed }));
            return prev;
          case 'error':
            return [...prev, { kind: 'notice', seq: e.seq, text: e.message, level: 'error' }];
          case 'shutdown':
            setBusy(false);
            onSessionEnded();
            return [
              ...prev,
              {
                kind: 'notice',
                seq: e.seq,
                text: `Session ended (${e.reason}). Your work is saved.`,
                level: 'info',
              },
            ];
          default:
            return prev;
        }
      });
    },
    [onSessionEnded],
  );

  // SSE from the sandbox, through the preview host. The browser's EventSource reconnects with Last-Event-ID.
  useEffect(() => {
    let es: EventSource | undefined;
    let stopped = false;
    let retry = 1000;
    const connect = () => {
      if (stopped) return;
      es = new EventSource(`${previewOrigin}${AGENT_PATHS.events}?since=${lastSeq.current}`, {
        withCredentials: true,
      });
      es.onopen = () => {
        setConnected(true);
        retry = 1000;
      };
      es.onmessage = (m) => apply(JSON.parse(m.data) as AgentEvent);
      for (const t of [
        'hello',
        'user',
        'assistant.delta',
        'assistant.message',
        'tool.start',
        'tool.end',
        'preview.status',
        'publish.progress',
        'turn.complete',
        'usage',
        'error',
        'shutdown',
      ]) {
        es.addEventListener(t, (m) => apply(JSON.parse((m as MessageEvent).data) as AgentEvent));
      }
      es.addEventListener('reconnect', () => {
        es?.close();
        connect();
      });
      es.onerror = () => {
        setConnected(false);
        es?.close();
        if (!stopped) {
          retry = Math.min(retry * 2, 15_000);
          setTimeout(connect, retry);
        }
      };
    };
    connect();
    return () => {
      stopped = true;
      es?.close();
    };
  }, [previewOrigin, apply]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: scroll on new items
  useEffect(() => {
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [items]);

  const post = useCallback(
    async (path: string, body?: unknown) => {
      const res = await fetch(`${previewOrigin}${path}`, {
        method: 'POST',
        credentials: 'include',
        headers: body ? { 'content-type': 'application/json' } : {},
        body: body ? JSON.stringify(body) : undefined,
      });
      if (!res.ok) {
        const j = (await res.json().catch(() => ({}))) as { error?: { message?: string } };
        throw new Error(j.error?.message ?? `Request failed (${res.status})`);
      }
    },
    [previewOrigin],
  );

  const send = async (e: FormEvent) => {
    e.preventDefault();
    const text = draft.trim();
    if (!text || busy) return;
    setError(undefined);
    try {
      setDraft('');
      await post(AGENT_PATHS.message, { text });
    } catch (err) {
      setError(err);
      setDraft(text);
    }
  };

  const publishing = useMemo(
    () => items.some((i) => i.kind === 'publish' && !['live', 'failed'].includes(i.phase)),
    [items],
  );

  return (
    <div className="builder">
      <section className="card builder-chat">
        <div className="builder-log" ref={listRef} role="log" aria-live="polite">
          {items.length === 0 ? (
            <p className="subtle">
              Tell the agent what to build. For example:{' '}
              <em>
                “A page where my clients can see their unpaid invoices and mark which ones they have queried.”
              </em>
            </p>
          ) : null}
          {items.map((i) => (
            <ChatLine key={i.seq} item={i} />
          ))}
          {busy ? <p className="small subtle">The agent is working…</p> : null}
        </div>
        <ErrorNotice error={error} />
        <form className="builder-compose" onSubmit={(e) => void send(e)}>
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder={busy ? 'Wait for the agent to finish, or stop it…' : 'What should the app do?'}
            rows={3}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                void send(e as unknown as FormEvent);
              }
            }}
          />
          <div className="btn-row">
            <button type="submit" className="btn btn-primary" disabled={busy || !draft.trim() || !connected}>
              Send
            </button>
            {busy ? (
              <button
                type="button"
                className="btn"
                onClick={() => void post(AGENT_PATHS.stop).catch(setError)}
              >
                Stop
              </button>
            ) : null}
            <button
              type="button"
              className="btn"
              disabled={busy || publishing || !connected}
              onClick={() => void post(AGENT_PATHS.publish).catch(setError)}
              title="Check the app against the hosting rules and deploy it to its live address"
            >
              Publish
            </button>
            <span className="small subtle" style={{ marginLeft: 'auto' }}>
              {connected ? '' : 'connecting… '}
              {usage.tokens ? `${Math.round(usage.tokens / 1000)}k tokens` : ''}
              {usage.cost !== undefined ? ` · $${usage.cost.toFixed(2)}` : ''}
            </span>
          </div>
        </form>
      </section>
      <section className="card builder-preview">
        <div className="btn-row" style={{ justifyContent: 'space-between' }}>
          <span className="small">
            Preview:{' '}
            <span
              className={`pill pill-${preview.state === 'ready' ? 'live' : preview.state === 'crashed' ? 'failed' : 'building'}`}
            >
              {preview.state}
            </span>
            {preview.detail ? <span className="subtle"> · {preview.detail}</span> : null}
          </span>
          <span className="btn-row">
            <button type="button" className="btn btn-sm" onClick={() => setFrameKey((k) => k + 1)}>
              Reload
            </button>
            <a className="btn btn-sm" href={`${previewOrigin}/`} target="_blank" rel="noreferrer">
              Open ↗
            </a>
          </span>
        </div>
        <iframe key={frameKey} title="App preview" src={`${previewOrigin}/`} className="builder-frame" />
        <p className="small subtle" style={{ margin: '0.5rem 0 0' }}>
          Session {session.id} · started {fmtTime(session.startedAt)}
        </p>
      </section>
    </div>
  );
}

function ChatLine({ item }: { item: ChatItem }) {
  switch (item.kind) {
    case 'user':
      return (
        <div className="chat chat-user">
          <div className="chat-text">{item.text}</div>
        </div>
      );
    case 'assistant':
      return (
        <div className="chat chat-assistant">
          <div className="chat-text" style={{ whiteSpace: 'pre-wrap' }}>
            {item.text}
            {item.streaming ? <span className="subtle">▍</span> : null}
          </div>
        </div>
      );
    case 'tool':
      return (
        <div className={`chat chat-tool ${item.done ? (item.ok ? 'ok' : 'failed') : 'running'}`}>
          <span className="mono small">
            {item.done ? (item.ok ? '✓' : '✗') : '…'} {prettyTool(item.name)}
            {item.summary ? <span className="subtle"> {item.summary}</span> : null}
          </span>
        </div>
      );
    case 'publish':
      return (
        <div className={`chat chat-publish ${item.phase}`}>
          <span className="small">
            <strong>Publish</strong> · {item.phase}: {item.message}
          </span>
        </div>
      );
    case 'notice':
      return <Notice kind={item.level === 'error' ? 'warn' : 'info'}>{item.text}</Notice>;
  }
}

function prettyTool(name: string): string {
  const map: Record<string, string> = {
    Read: 'Reading',
    Write: 'Writing',
    Edit: 'Editing',
    MultiEdit: 'Editing',
    Bash: 'Running',
    Glob: 'Finding files',
    Grep: 'Searching',
    TodoWrite: 'Planning',
    mcp__xb__check_contract: 'Checking hosting rules',
    mcp__xb__publish: 'Publishing',
    mcp__xb__preview_status: 'Checking the preview',
    mcp__xb__restart_preview: 'Restarting the preview',
  };
  return map[name] ?? name;
}

export { errorMessage as _errorMessage };
